/**
 * Development-only logger utility for routing and auth debugging.
 *
 * All logs are automatically gated behind __DEV__ to prevent noise in production builds.
 * This centralizes logging configuration and makes it easy to disable if needed.
 */

interface Logger {
  log: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
}

/**
 * Creates a scoped logger with a prefix for easier filtering in console.
 *
 * @param scope - Prefix to add to all log messages (e.g., 'USE_AUTH', 'ROUTING')
 * @returns Logger instance with log, warn, and error methods
 *
 * @example
 * const logger = createLogger('USE_AUTH');
 * logger.log('User logged in'); // Only logs in __DEV__ mode
 */
export function createLogger(scope: string): Logger {
  const prefix = `[${scope}]`;

  return {
    log: (...args: any[]) => {
      if (__DEV__) {
        console.log(prefix, ...args);
      }
    },
    warn: (...args: any[]) => {
      if (__DEV__) {
        console.warn(prefix, ...args);
      }
    },
    error: (...args: any[]) => {
      // Errors are always logged, even in production, but with dev-only prefix
      if (__DEV__) {
        console.error(prefix, ...args);
      } else {
        // In production, log errors without the prefix to reduce noise
        console.error(...args);
      }
    },
  };
}

/**
 * Default logger for general use (no scope prefix).
 */
export const logger = createLogger('APP');
